import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Zap, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditPage() {
  const { slug } = useParams();
  const [magnet, setMagnet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    consent_to_contact: false,
  });

  useEffect(() => {
    axios.get(`${API}/public/lead-magnets/${slug}`)
      .then(r => setMagnet(r.data))
      .catch(() => toast.error('Offer not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_to_contact) {
      toast.error('Please confirm consent to be contacted');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/audit-requests`, {
        ...form,
        lead_magnet_slug: slug,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!magnet) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-slate-500">This offer is no longer available.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="audit-success">
        <Card className="max-w-md w-full mx-4 border-slate-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600 text-sm">We've received your request and will be in touch within 24 hours with your free audit report.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="audit-page">
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <Zap className="w-6 h-6 text-orange-500" />
          <span className="font-black text-lg tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Offer Copy */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              {magnet.name}
            </h1>
            <div className="prose prose-sm text-slate-600">
              {magnet.offer_md.split('\n').map((line, i) => {
                if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-slate-800 mt-4 mb-2">{line.replace('## ', '')}</h2>;
                if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold text-slate-700 mt-3">{line.replace(/\*\*/g, '')}</p>;
                if (line.startsWith('- ')) return <p key={i} className="flex items-start gap-2 text-slate-600"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{line.replace('- ', '')}</p>;
                if (line.trim()) return <p key={i}>{line}</p>;
                return null;
              })}
            </div>
          </div>

          {/* Form */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-slate-900">Request Your Free Audit</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid gap-3">
                <div>
                  <Label className="text-slate-600 text-xs">Company Name *</Label>
                  <Input className="h-9 rounded-sm mt-1" required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} data-testid="audit-company-input" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs">Your Name *</Label>
                  <Input className="h-9 rounded-sm mt-1" required value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} data-testid="audit-name-input" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs">Email *</Label>
                  <Input type="email" className="h-9 rounded-sm mt-1" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="audit-email-input" />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs">Phone (optional)</Label>
                  <Input className="h-9 rounded-sm mt-1" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="audit-phone-input" />
                </div>
                <div className="flex items-start gap-2 mt-2">
                  <Checkbox
                    checked={form.consent_to_contact}
                    onCheckedChange={v => setForm({...form, consent_to_contact: !!v})}
                    data-testid="audit-consent-checkbox"
                  />
                  <label className="text-xs text-slate-500 leading-relaxed cursor-pointer" onClick={() => setForm({...form, consent_to_contact: !form.consent_to_contact})}>
                    I consent to BidSentry contacting me about this audit and related services. *
                  </label>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-sm mt-2"
                  disabled={submitting || !form.company_name || !form.contact_name || !form.email || !form.consent_to_contact}
                  data-testid="audit-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Get My Free Audit
                </Button>
                <p className="text-[10px] text-slate-400 text-center">We respect your data. No spam, ever.</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
